import { computed, Injectable, signal } from '@angular/core';
import {
  type AccountInfo,
  type AuthenticationResult,
  type RedirectRequest,
  InteractionRequiredAuthError,
  BrowserAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AUTH_CONFIG } from '../config';

type AuthUser = {
  name: string;
  email: string;
  objectId?: string;
};

const AUTH_STORAGE_KEY = AUTH_CONFIG.storageKey;
const LOGIN_BLOCK_REASON_KEY = `${AUTH_STORAGE_KEY}:login-block-reason`;
type LoginBlockReason = 'suspended';

@Injectable({
  providedIn: 'root',
})
export class Auth {
  readonly #user = signal<AuthUser | null>(this.#readStoredUser());
  readonly #token = signal<string | null>(null);
  readonly #client =
    typeof window === 'undefined'
      ? null
      : new PublicClientApplication({
          auth: {
            authority: AUTH_CONFIG.entra.authority,
            clientId: AUTH_CONFIG.entra.clientId,
            redirectUri: `${window.location.origin}${AUTH_CONFIG.entra.redirectPath}`,
            postLogoutRedirectUri: `${window.location.origin}/login`,
          },
          cache: {
            cacheLocation: 'localStorage',
          },
        });
  readonly #loginRequest: RedirectRequest = {
    scopes: [...AUTH_CONFIG.entra.scopes],
    prompt: 'select_account',
  };
  readonly #initializePromise = this.#initialize();

  readonly user = computed(() => this.#user());
  readonly token = computed(() => this.#token());
  readonly isAuthenticated = computed(() => Boolean(this.#user() && this.#token()));

  async login() {
    if (!this.#client) {
      throw new Error('Microsoft Entra authentication is not available in this environment.');
    }

    await this.#initializePromise;
    this.clearLoginBlockReason();
    this.#client.setActiveAccount(null);
    await this.#client.clearCache();
    await this.#client.loginRedirect(this.#loginRequest);
  }

  async loginDemo() {
    this.clearLoginBlockReason();
    const user = {
      name: 'Demo',
      email: AUTH_CONFIG.entra.demoLoginHint,
      objectId: '93439066-7cb0-4b9b-85e6-c1ea6ae10801',
    };
    this.#client?.setActiveAccount(null);
    this.#user.set(user);
    this.#token.set('sunbrick-demo-readonly');
    this.#persistSession(user);
  }

  async initializeAuth() {
    await this.#initializePromise;
    return this.isAuthenticated();
  }

  async getAccessToken() {
    await this.#initializePromise;

    if (this.#token() === 'sunbrick-demo-readonly') {
      return this.#token();
    }

    if (!this.#client) {
      return this.#token();
    }

    const account = this.#getActiveAccount();
    if (!account) {
      return null;
    }

    try {
      const result = await this.#client.acquireTokenSilent({
        account,
        scopes: [...AUTH_CONFIG.entra.scopes],
      });

      await this.#hydrateSession(result);
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        this.#clearSession();
        return null;
      }

      throw error;
    }
  }

  async logout() {
    this.#clearSession();

    if (!this.#client) {
      return;
    }

    await this.#client.clearCache();
  }

  expireSession() {
    this.#clearSession();
    this.#client?.setActiveAccount(null);
    void this.#client?.clearCache();
  }

  setLoginBlockReason(reason: LoginBlockReason) {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(LOGIN_BLOCK_REASON_KEY, reason);
  }

  consumeLoginBlockReason(): LoginBlockReason | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const reason = window.sessionStorage.getItem(LOGIN_BLOCK_REASON_KEY);
    window.sessionStorage.removeItem(LOGIN_BLOCK_REASON_KEY);
    return reason === 'suspended' ? reason : null;
  }

  clearLoginBlockReason() {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.removeItem(LOGIN_BLOCK_REASON_KEY);
  }

  #readStoredUser() {
    if (typeof window === 'undefined') {
      return null;
    }

    const storedValue = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue) as AuthUser;
    } catch {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  }

  #persistUser(user: AuthUser) {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
  }

  #persistSession(user: AuthUser) {
    this.#persistUser(user);
  }

  #clearSession() {
    this.#user.set(null);
    this.#token.set(null);

    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  async #initialize() {
    if (!this.#client) {
      return;
    }

    await this.#client.initialize();

    const redirectResult = await this.#client.handleRedirectPromise();
    const resultAccount = redirectResult?.account;
    const activeAccount = resultAccount ?? this.#getActiveAccount();

    if (resultAccount) {
      this.#client.setActiveAccount(resultAccount);
    }

    if (!activeAccount) {
      return;
    }

    try {
      const token = await this.#client.acquireTokenSilent({
        account: activeAccount,
        scopes: [...AUTH_CONFIG.entra.scopes],
      });
      await this.#hydrateSession(token);
    } catch (error) {
      const requiresLogin = error instanceof InteractionRequiredAuthError;
      const silentRequestTimedOut = error instanceof BrowserAuthError && error.errorCode === 'timed_out';
      if (!requiresLogin && !silentRequestTimedOut) {
        throw error;
      }
      this.#clearSession();
      this.#client.setActiveAccount(null);
    }
  }

  #getActiveAccount() {
    if (!this.#client) {
      return null;
    }

    return this.#client.getActiveAccount() ?? this.#client.getAllAccounts()[0] ?? null;
  }

  async #hydrateSession(result: AuthenticationResult) {
    const account = result.account;
    if (!account?.username || !result.accessToken) {
      return;
    }

    const user = this.#mapAccountToUser(account);

    this.#user.set(user);
    this.#token.set(result.accessToken);
    this.#persistSession(user);
    this.#logSession(result, user);
  }

  #mapAccountToUser(account: AccountInfo): AuthUser {
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const nameClaim = claims?.['name'];
    const emailClaim = claims?.['preferred_username'];
    const objectIdClaim = claims?.['oid'];

    return {
      name: typeof nameClaim === 'string' && nameClaim.length > 0 ? nameClaim : account.name ?? account.username,
      email: typeof emailClaim === 'string' && emailClaim.length > 0 ? emailClaim : account.username,
      objectId: typeof objectIdClaim === 'string' ? objectIdClaim : undefined,
    };
  }

  #logSession(result: AuthenticationResult, user: AuthUser) {
    if (typeof window === 'undefined' || !AUTH_CONFIG.debugSessionLogging) {
      return;
    }

    console.group('Sunbrick ERP auth session');
    console.log('user', user);
    console.log('accessToken', result.accessToken);
    console.log('account', result.account);
    console.log('idTokenClaims', result.account?.idTokenClaims ?? null);
    console.groupEnd();
  }
}
